import { MAX_OPEN_PROJECT_NAME_LENGTH, OpenProjectKind } from '../models/user-settings.model';
import { EmbeddedAuthPayload, isEmbeddedAuthProfileSource, pickEmbeddedProfile } from './embedded-auth';

export const EDITOR_BRIDGE_VERSION = 1;

/** The editor URL query parameter that asks for embedded mode (`?embedded=1`). */
export const EMBEDDED_QUERY_PARAM = 'embedded';

export type BridgeSource = 'infinitform-editor' | 'infinitform-cabinet';

export type BridgeThemeMode = 'light' | 'dark' | 'system';

export type HostToEditorMessage =
  | { type: 'auth'; payload: EmbeddedAuthPayload }
  | { type: 'pause'; payload: null }
  | { type: 'resume'; payload: null }
  | { type: 'snapshot-request'; payload: null }
  | { type: 'hibernate'; payload: null }
  | { type: 'logout'; payload: null }
  | { type: 'theme'; payload: { mode: BridgeThemeMode } };

export type EditorToHostMessage =
  | { type: 'ready'; payload: { protocol: number } }
  | { type: 'meta'; payload: { projectId: string; name: string; kind: OpenProjectKind } }
  | { type: 'dirty'; payload: { value: boolean } }
  /** True while the editor is still loading its project, false once it shows it or gave up. */
  | { type: 'loading'; payload: { value: boolean } }
  | { type: 'snapshot'; payload: { blob: Blob } }
  | { type: 'hibernate-ack'; payload: null }
  | { type: 'navigate'; payload: { cabinetPath: string } }
  | { type: 'not-found'; payload: null }
  | { type: 'forbidden'; payload: null }
  | { type: 'title'; payload: { text: string } }
  | { type: 'auth-request'; payload: null }
  | { type: 'close'; payload: null };

const KINDS: readonly OpenProjectKind[] = ['editor', 'mold', 'jaws'];
const THEME_MODES: readonly BridgeThemeMode[] = ['light', 'dark', 'system'];
const MAX_PATH_LENGTH = 500;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readEnvelope(data: unknown, expected: BridgeSource): { type: string; payload: unknown } | null {
  if (!isRecord(data)) return null;
  if (data['source'] !== expected || data['v'] !== EDITOR_BRIDGE_VERSION) return null;
  const type = data['type'];
  return typeof type === 'string' ? { type, payload: data['payload'] } : null;
}

function boundedText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text ? text.slice(0, MAX_OPEN_PROJECT_NAME_LENGTH) : null;
}

/** A path inside the cabinet: absolute, not protocol-relative, no backslashes. */
export function isCabinetPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= MAX_PATH_LENGTH &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\')
  );
}

/** A message the cabinet sent to an editor, or null for anything else. */
export function parseHostToEditor(data: unknown): HostToEditorMessage | null {
  const envelope = readEnvelope(data, 'infinitform-cabinet');
  if (!envelope) return null;
  const { type, payload } = envelope;
  switch (type) {
    case 'auth': {
      if (!isRecord(payload)) return null;
      const token = payload['token'];
      const expiresAt = payload['expiresAt'];
      const profile = payload['profile'];
      if (typeof token !== 'string' || !token) return null;
      if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return null;
      if (!isEmbeddedAuthProfileSource(profile)) return null;
      return { type, payload: { token, expiresAt, profile: pickEmbeddedProfile(profile) } };
    }
    case 'pause':
    case 'resume':
    case 'snapshot-request':
    case 'hibernate':
    case 'logout':
      return { type, payload: null };
    case 'theme': {
      const mode = isRecord(payload) ? THEME_MODES.find((known) => known === payload['mode']) : undefined;
      return mode ? { type, payload: { mode } } : null;
    }
    default:
      return null;
  }
}

/** A message an editor sent to the cabinet, or null for anything else. */
export function parseEditorToHost(data: unknown): EditorToHostMessage | null {
  const envelope = readEnvelope(data, 'infinitform-editor');
  if (!envelope) return null;
  const { type, payload } = envelope;
  switch (type) {
    case 'ready':
      return isRecord(payload) && payload['protocol'] === EDITOR_BRIDGE_VERSION
        ? { type, payload: { protocol: EDITOR_BRIDGE_VERSION } }
        : null;
    case 'meta': {
      if (!isRecord(payload)) return null;
      const projectId = payload['projectId'];
      const name = boundedText(payload['name']);
      const kind = KINDS.find((known) => known === payload['kind']);
      return typeof projectId === 'string' && name && kind ? { type, payload: { projectId, name, kind } } : null;
    }
    case 'dirty':
    case 'loading':
      return isRecord(payload) && typeof payload['value'] === 'boolean' ? { type, payload: { value: payload['value'] } } : null;
    case 'snapshot': {
      const blob = isRecord(payload) ? payload['blob'] : undefined;
      return typeof Blob !== 'undefined' && blob instanceof Blob ? { type, payload: { blob } } : null;
    }
    case 'navigate': {
      const cabinetPath = isRecord(payload) ? payload['cabinetPath'] : undefined;
      return isCabinetPath(cabinetPath) ? { type, payload: { cabinetPath } } : null;
    }
    case 'title': {
      const text = isRecord(payload) ? boundedText(payload['text']) : null;
      return text ? { type, payload: { text } } : null;
    }
    case 'hibernate-ack':
    case 'not-found':
    case 'forbidden':
    case 'auth-request':
    case 'close':
      return { type, payload: null };
    default:
      return null;
  }
}

/** True for an editor running in a frame that asked for embedded mode with `?embedded=1`. */
export function isEmbeddedEditor(win: { location: { search: string }; parent: unknown }): boolean {
  return win.parent !== win && new URLSearchParams(win.location.search).get(EMBEDDED_QUERY_PARAM) === '1';
}
